import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  input,
  output,
} from '@angular/core';
import {
  TwTabsComponent,
  type TwTabsItem,
} from '@twist/ui-primitives';
import { TwNavTabsComponent } from './tw-nav-tabs.component';

/**
 * `tw-page-header-tabs` — tabs row for {@link
 * import('./tw-page-header.component').TwPageHeaderComponent}.
 *
 * Thin wrapper around the shipped `<tw-tabs>` primitive (backed by
 * `@angular/aria/tabs`) that integrates the tabs nav with the page-header's
 * bottom-edge treatment. When this sub-component is present, the parent
 * `<tw-page-header>` suppresses its own `border-bottom` (via
 * `:has(tw-page-header-tabs)`) so the tabs nav's own border becomes the
 * bottom rule of the header — no double rule, no extra padding.
 *
 * The wrapper exposes the same `tabs` + `activeId` inputs and
 * `(activeIdChange)` output as the underlying `<tw-tabs>` so consumers can
 * write `<tw-page-header-tabs [tabs]="tabs" [(activeId)]="activeTab" />`.
 * The animated active underline ships with `tw-tabs` itself.
 *
 * ```html
 * <tw-page-header-tabs
 *   [tabs]="[
 *     { id: 'overview', label: 'Overview' },
 *     { id: 'fields',   label: 'Fields' },
 *   ]"
 *   [(activeId)]="activeTab"
 * />
 * ```
 *
 * Count pills are supported via `TwTabsItem.count` on the shared `tw-tabs`
 * primitive itself (so every `tw-tabs` consumer benefits) and flow through
 * this thin wrapper automatically — pass `{ id, label, count }` items and
 * the pill renders next to the label (accent-tinted when active).
 *
 * ### Routed mode (`[navId]`)
 *
 * Setting the optional `navId` input switches this wrapper into ROUTED
 * mode: instead of the `activeId` content-switch `<tw-tabs>`, it renders
 * `<tw-nav-tabs [navId]>` — registry-fed `<a routerLink>` tabs with
 * `aria-current` (see {@link TwNavTabsComponent}). The `tabs` / `activeId`
 * / `activeIdChange` content-switch API is left fully intact for existing
 * consumers (CIAM, products); `navId` simply takes precedence when set.
 */
@Component({
  selector: 'tw-page-header-tabs',
  standalone: true,
  imports: [TwTabsComponent, TwNavTabsComponent],
  templateUrl: './tw-page-header-tabs.component.html',
  host: {
    'class': 'tw-page-header-tabs',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class TwPageHeaderTabsComponent {
  /** Tab definitions — forwarded directly to the underlying `<tw-tabs>`. */
  readonly tabs = input<TwTabsItem[]>([]);

  /** Currently-active tab id. Pair with `(activeIdChange)` for two-way `[(activeId)]`. */
  readonly activeId = input<string | undefined>(undefined);

  /** Emitted when the user activates a different tab. */
  readonly activeIdChange = output<string>();

  /**
   * Optional parent navigation descriptor id. When set, the wrapper
   * renders routed tabs (`<tw-nav-tabs [navId]>`) instead of the
   * `activeId` content-switch `<tw-tabs>`. Leave unset for the
   * existing content-switch behaviour.
   */
  readonly navId = input<string | undefined>(undefined);

  /** True when the wrapper is in routed mode (a `navId` was supplied). */
  protected readonly routed = computed<boolean>(() => !!this.navId());

  protected onActiveIdChange(next: string): void {
    this.activeIdChange.emit(next);
  }
}
